// beatGrid — a song, measured: its tempo, where each bar starts, and what each
// bar is like (how loud, how hard the kick lands, how busy, which sixteenths the
// drums hit). The plaza stage's scene view cuts to it (city/stageCuts).
//
// Khari, 2026-10-28: *"when the render-bot was built, claude had created an
// amazing motion graphic that used the analysis of the audio to time the
// animation… We should do the same for our performances here. Analyze the song
// and the genre. If the song requires a lot of movement, simulate multiple live
// cameras. Whip pan onto a closeup of the artists face.. change shots rapidly
// based on the drum pattern, etc.."*
//
// THE TEMPO AND THE BAR are the render-bot's measurement (render-bot
// worker/media.js measureGrid): an onset envelope (the kick band counted double),
// a comb search for the tempo, and the downbeat voted for by where the loudness
// jumps (sections change on a bar). Two changes: it searches 70–160 BPM, not
// 100–150 (our artists play hip-hop and R&B at 70–100, which that range could
// only read at the wrong speed), coarse then fine, so it is cheap enough to run
// in a browser.
//
// PURE: samples in, numbers out — no fetching, no audio API (city/songAnalysis
// does that), so the same code can be checked against real songs in Node.
//
// ⚠ A TWIN LIVES IN THE API (src/utils/beatGrid.js: a freestyle's beat, measured
// on the server so the verses land on its bars). CHANGE THEM TOGETHER.

export const SR = 11025;           // the rate it measures at (callers resample to it)
const HOP = 64;                    // one frame = 5.8 ms
const WIN = 256;

const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
function pct(arr, p) {
  if (!arr.length) return 0;
  const s = Float64Array.from(arr).sort();
  return s[Math.min(s.length - 1, Math.max(0, Math.round((s.length - 1) * p)))];
}

/**
 * @param x     mono samples at SR
 * @param opts  { range: [lo, hi] } BPM to look in
 * @returns { bpm, offset, beat, bar, dur, beats, drive, density, bars: [{ t, len, e, punch, busy, kick, snare }] } | null
 *   offset — the time of a downbeat (s), folded into (−bar/2, bar/2]
 *   beats  — every beat (s), followed through the song (not a fixed grid)
 *   hits   — { kick: [s], snare: [s] } the strongest drum hits, for cuts to land on
 *   bars   — every fourth beat from the first downbeat; e/punch/busy are 0..1 within this
 *            song; kick/snare are 16-bit masks of the sixteenths where they hit
 *   drive  — 0..1 across songs: how much movement this song asks for
 */
export function measureSong(x, opts = {}) {
  const n = Math.floor((x.length - WIN) / HOP);
  if (n < 400) return null;
  const fps = SR / HOP;
  const dur = x.length / SR;

  // Three bands from two one-pole filters: everything, below ~140 Hz (the kick),
  // above ~900 Hz (snare crack, claps, hats).
  const eAll = new Float32Array(n); const eLow = new Float32Array(n); const eHi = new Float32Array(n);
  {
    const lp = new Float32Array(x.length); const hp = new Float32Array(x.length);
    let y = 0; let z = 0;
    for (let i = 0; i < x.length; i++) { y += 0.08 * (x[i] - y); z += 0.5 * (x[i] - z); lp[i] = y; hp[i] = x[i] - z; }
    for (let f = 0; f < n; f++) {
      let a = 0; let b = 0; let c = 0; const o = f * HOP;
      for (let k = 0; k < WIN; k++) { const s = x[o + k]; const l = lp[o + k]; const h = hp[o + k]; a += s * s; b += l * l; c += h * h; }
      eAll[f] = Math.log(1e-9 + a); eLow[f] = Math.log(1e-9 + b); eHi[f] = Math.log(1e-9 + c);
    }
  }
  const on = new Float32Array(n); const onLow = new Float32Array(n); const onHi = new Float32Array(n);
  for (let f = 1; f < n; f++) {
    onLow[f] = Math.max(0, eLow[f] - eLow[f - 1]);
    onHi[f] = Math.max(0, eHi[f] - eHi[f - 1]);
    on[f] = onLow[f] + 0.5 * Math.max(0, eAll[f] - eAll[f - 1]);
  }

  // ── Tempo and phase: the comb that lines up with the most onsets ──────────
  const at = (t) => { const i = Math.round(t); return (on[i] || 0) + 0.5 * (on[i - 1] || 0) + 0.5 * (on[i + 1] || 0); };
  // Over frames [a, b): the coarse search reads the middle 90 s only (the same
  // answer, a third of the work), the fine search the whole song.
  const comb = (bpm, ph, every = 1, a = 0, b = n) => {
    const p = (60 / bpm) * fps * every; let s = 0; let c = 0;
    for (let t = ph + Math.max(0, Math.ceil((a - ph) / p)) * p; t < b; t += p) { s += at(t); c++; }
    return s / Math.max(1, c);
  };
  // THE WHOLE METRE, NOT ONE PULSE: a tempo scores with its eighths and its
  // sixteenths too. A single comb read a 124 BPM electronic track as 93 (a 3:4
  // lock on a repeating figure); at 93 its eighths and sixteenths fall between
  // the hits, at 124 they land on the hats. And a gentle lean toward the middle
  // of the range (a pulse an octave away is the same song, rarely the felt one).
  const score = (bpm, ph, a, b) => (comb(bpm, ph, 1, a, b) + 0.5 * comb(bpm * 2, ph, 1, a, b) + 0.25 * comb(bpm * 4, ph, 1, a, b))
    * Math.exp(-0.5 * Math.log2(bpm / 115) ** 2);
  const search = (lo, hi, step, phStep, a, b, best = { s: -1 }) => {
    for (let bpm = lo; bpm <= hi + 1e-9; bpm += step) {
      const p = (60 / bpm) * fps;
      for (let ph = 0; ph < p; ph += phStep) { const s = score(bpm, ph, a, b); if (s > best.s) best = { s, bpm, ph }; }
    }
    return best;
  };
  const [lo, hi] = opts.range || [70, 160];
  const mid0 = Math.max(0, Math.floor(n / 2 - 45 * fps)); const mid1 = Math.min(n, mid0 + Math.round(90 * fps));
  let best = search(lo, hi, 0.5, 2, mid0, mid1);
  best = search(Math.max(lo, best.bpm - 0.6), Math.min(hi, best.bpm + 0.6), 0.02, 1, 0, n, { s: -1 });
  const beat = 60 / best.bpm; const bar = beat * 4; const first = best.ph / fps;

  // ── Which beat is "one": where the loudness jumps (drops, builds) ─────────
  const rms = [];
  for (let t = first; t + beat <= dur; t += beat) {
    let a = 0; const i0 = Math.floor(t * SR); const i1 = Math.floor((t + beat) * SR);
    for (let i = i0; i < i1; i++) a += x[i] * x[i];
    rms.push(10 * Math.log10(a / Math.max(1, i1 - i0) + 1e-12));
  }
  const jumps = rms.map((v, i) => ({ i, d: i ? v - rms[i - 1] : 0 })).filter((j) => j.d >= 3).sort((p, q) => q.d - p.d).slice(0, 4);
  const votes = [0, 0, 0, 0];
  jumps.forEach((j) => { votes[j.i % 4] += j.d; });
  let down = { s: -1, j: 0 };
  if (jumps.length) votes.forEach((v, j) => { if (v > down.s) down = { s: v, j }; });
  else for (let j = 0; j < 4; j++) { const s = comb(best.bpm, best.ph + j * beat * fps, 4); if (s > down.s) down = { s, j }; }
  let offset = first + down.j * beat;
  offset -= Math.round(offset / bar) * bar;

  // ── FOLLOW THE BEAT ──────────────────────────────────────────────────────
  // One fixed grid drifts: 0.8 BPM off on a 4½-minute song is two seconds by
  // the end, cuts landing between beats. So each beat is PREDICTED from the
  // last, then pulled part of the way onto the strongest KICK near it (when there
  // is one), and the tempo follows slowly — never more than 2.5% from the one
  // measured. Kicks only: pulled by every onset, an afrobeat track's off-beat
  // shakers walked the grid half a beat off over a minute (measured against
  // essentia's beat tracker, 2026-10-28).
  const frame = (t) => Math.max(0, Math.min(n - 1, Math.round(t * fps)));
  const atLow = (f) => (onLow[f] || 0) + 0.5 * (onLow[f - 1] || 0) + 0.5 * (onLow[f + 1] || 0);
  const hitThr = pct(Array.from(onLow).filter((v) => v > 0), 0.7);
  const beats = [];      // times (s) of every beat from the first downbeat at or after 0
  {
    let i = Math.max(0, Math.ceil(-offset / beat));
    i += (4 - (i % 4)) % 4;                              // start on a downbeat
    let period = beat; let tb = offset + i * beat;
    const reach = beat * 0.08;
    for (; tb < dur - 0.05 && beats.length < 4000; i++) {
      let bestF = -1; let bestV = hitThr;
      for (let f = frame(tb - reach); f <= frame(tb + reach); f++) { const v = atLow(f); if (v > bestV) { bestV = v; bestF = f; } }
      const t = bestF >= 0 ? tb + 0.35 * (bestF / fps - tb) : tb;
      if (beats.length) period = Math.min(beat * 1.025, Math.max(beat * 0.975, period + 0.05 * ((t - beats[beats.length - 1]) - period)));
      beats.push(t);
      tb = t + period;
    }
  }

  // ── Each bar: loudness, the kick's punch, how busy, the drum sixteenths ───
  const peakThr = Math.max(0.35, pct(Array.from(on).filter((v) => v > 0), 0.9));
  let peaks = 0;
  for (let f = 1; f < n - 1; f++) if (on[f] > peakThr && on[f] > on[f - 1] && on[f] >= on[f + 1]) peaks++;
  const raw = [];
  for (let k = 0; k + 1 < beats.length; k += 4) {
    const t = beats[k];
    const len = beats[k + 4] != null ? beats[k + 4] - t : bar;
    const f0 = frame(t); const f1 = frame(t + len);
    let e = 0; let c = 0; let busy = 0;
    for (let f = Math.max(1, f0); f < f1 && f < n - 1; f++) {
      e += eAll[f]; c++;
      if (on[f] > peakThr && on[f] > on[f - 1] && on[f] >= on[f + 1]) busy++;
    }
    let punch = 0;
    for (let b = 0; b < 4; b++) {
      const fb = frame(beats[k + b] ?? t + b * beat); let m = 0;
      for (let f = fb - 2; f <= fb + 2; f++) m = Math.max(m, onLow[f] || 0);
      punch += m / 4;
    }
    const sLow = new Float32Array(16); const sHi = new Float32Array(16);
    for (let j = 0; j < 16; j++) {
      const a = frame(t + (j * len) / 16) - 1; const b = frame(t + ((j + 1) * len) / 16) - 1;
      for (let f = a; f < b; f++) { sLow[j] = Math.max(sLow[j], onLow[f] || 0); sHi[j] = Math.max(sHi[j], onHi[f] || 0); }
    }
    raw.push({ t, len, e: c ? e / c : -20, punch, busy, sLow, sHi });
  }
  if (!raw.length) return null;

  // ── THE HITS THEMSELVES: where a cut lands ───────────────────────────────
  // A cut snaps to the real kick or snare nearest where the grid puts it, so it
  // lands ON the drum even where the grid is a sixteenth off.
  // Only the strong ones, never two within 90 ms (a flam is one hit).
  const peaksOf = (env, thr) => {
    const out = [];
    for (let f = 2; f < n - 2; f++) {
      if (!(env[f] > thr && env[f] > env[f - 1] && env[f] >= env[f + 1] && env[f] >= env[f - 2] && env[f] >= env[f + 2])) continue;
      const t = Math.round((f / fps) * 1000) / 1000;
      if (out.length && t - out[out.length - 1] < 0.09) continue;
      out.push(t);
    }
    return out;
  };
  const kicks = peaksOf(onLow, pct(Array.from(onLow).filter((v) => v > 0), 0.93));
  const snares = peaksOf(onHi, pct(Array.from(onHi).filter((v) => v > 0), 0.95));

  // Within this song: 0..1 between its quiet and its loud.
  const es = raw.map((r) => r.e); const eLo = pct(es, 0.1); const eTop = pct(es, 0.95);
  const pTop = pct(raw.map((r) => r.punch), 0.95) || 1;
  const bTop = pct(raw.map((r) => r.busy), 0.95) || 1;
  // A sixteenth "hits" when it is among the song's strongest (the kick at 80%;
  // the snare at 90%, so the steady hats do not count as accents).
  const loud = raw.filter((r) => r.e >= pct(es, 0.5));
  const kThr = pct(loud.flatMap((r) => Array.from(r.sLow)), 0.8);
  const sThr = pct(loud.flatMap((r) => Array.from(r.sHi)), 0.9);
  const bars = raw.map((r) => {
    let kick = 0; let snare = 0;
    for (let j = 0; j < 16; j++) { if (r.sLow[j] >= kThr && kThr > 0) kick |= 1 << j; if (r.sHi[j] >= sThr && sThr > 0) snare |= 1 << j; }
    return {
      t: Math.round(r.t * 1000) / 1000,
      len: Math.round(r.len * 1000) / 1000,
      e: Math.round(clamp((r.e - eLo) / Math.max(1e-6, eTop - eLo)) * 100) / 100,
      punch: Math.round(clamp(r.punch / pTop) * 100) / 100,
      busy: Math.round(clamp(r.busy / bTop) * 100) / 100,
      kick, snare,
    };
  });

  // Across songs: how much movement it asks for — busy drums, a hard kick, pace.
  const density = peaks / dur;                                  // onsets a second
  const punchAbs = pct(raw.map((r) => r.punch), 0.5);
  const drive = clamp(0.45 * clamp((density - 1) / 5) + 0.3 * clamp((punchAbs - 0.4) / 2) + 0.25 * clamp((best.bpm - 75) / 60));

  return {
    bpm: Math.round(best.bpm * 100) / 100,
    offset: Math.round(offset * 1000) / 1000,
    beat, bar, dur,
    beats: beats.map((b) => Math.round(b * 1000) / 1000),
    hits: { kick: kicks, snare: snares },
    drive: Math.round(drive * 100) / 100,
    density: Math.round(density * 100) / 100,
    punchAbs: Math.round(punchAbs * 100) / 100,
    bars,
  };
}
